import type { AnyRoute } from "@bingo/contract-fan-api";
import type { ResponsePromise } from "ky";
import { privateClient, publicClient } from "./client.ts";

/**
 * Reads the body from a ky response, returning `null` for 204 No Content (or
 * other empty bodies).
 *
 * ky v2 throws a SyntaxError when `.json()` is called on an empty body. Any
 * route whose response contract declares `body: null` (e.g., `POST /api/login`)
 * would otherwise fail with an unparseable-JSON error.
 */
async function parseResponseBody<T>(
  responsePromise: ResponsePromise,
): Promise<T> {
  const response = await responsePromise;
  if (
    response.status === 204 ||
    response.headers.get("content-length") === "0"
  ) {
    return null as T;
  }
  return (await response.json()) as T;
}

/**
 * Uses ky to make a request to the given API route.
 *
 * As appropriate, it:
 *
 * - Replaces the route's path parameters with the values supplied.
 * - Sets the request body.
 * - Passes request options to ky (e.g., `{ credentials: "same-origin" }`).
 * - Returns the response body, typed to the route's response body.
 *
 * @example
 * ```ts
 * const getGames = () => request<GameGetRoute>({
 *   method: "GET",
 *   path: "/api/games",
 * });
 * ```
 */
export function request<Route extends AnyRoute>(
  request: Route["request"] & {
    authenticate: boolean;
  },
  {
    options,
  }: {
    options?: Parameters<(typeof publicClient)["get"]>[1];
  } = {},
) {
  const client = request.authenticate ? privateClient : publicClient;

  const path = Object.entries(request.parameters ?? {}).reduce(
    (path, [key, value]) => path.replace(`:${key}`, value),
    request.path,
  );

  const searchParams = Object.entries(request.searchParams ?? {}).reduce(
    (params, [key, value]) => {
      // If the value is an array, append each value to the search params.
      if (Array.isArray(value)) {
        for (const v of value) params.append(key, v);
      } else {
        params.append(key, value);
      }

      return params;
    },
    new URLSearchParams(),
  );

  switch (request.method) {
    case "GET":
      return parseResponseBody<Route["response"]["body"]>(
        client.get(path, {
          searchParams,
          ...options,
        }),
      );
    case "POST":
      return parseResponseBody<Route["response"]["body"]>(
        client.post(path, {
          searchParams,
          json: request.body,
          ...options,
        }),
      );
    case "PUT":
      // We need to avoid PUT requests because they are not supported by the MLB
      // CDN. We should use POST instead.
      console.warn("Avoid using PUT in the API. Use POST instead.");
      return parseResponseBody<Route["response"]["body"]>(
        client.put(path, {
          searchParams,
          json: request.body,
          ...options,
        }),
      );
    case "DELETE":
      return parseResponseBody<Route["response"]["body"]>(
        client.delete(path, {
          searchParams,
          ...options,
        }),
      );
  }
}
